import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { H4, Stack, useTheme } from '@qred/components-library';
import React from 'react';
import useTranslate from '~/hooks/useTranslate';
import { isInIframe } from '~/iframeMode';
import { StandardPageContainer } from '~/styles/BaseStyledComponents';
import { DotsStepper } from '../DotsStepper/DotsStepper';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { CardApplicationResult } from './components/CardApplicationResult';
import { useCardApplicationWebSocket } from './hooks/useCardApplicationWebSocket/useCardApplicationWebSocket';
import { useFakeProgress } from './hooks/useFakeProgress/useFakeProgress';

const steps: Array<{ title: string; lottieSrc: string }> = [
  {
    title: 'Onboarding.AutoProcessingStep1Title',
    lottieSrc: '/lottieFiles/general/person.lottie',
  },
  {
    title: 'Onboarding.AutoProcessingStep2Title',
    lottieSrc: '/lottieFiles/general/corporate_fare.lottie',
  },
  {
    title: 'Onboarding.AutoProcessingStep3Title',
    lottieSrc: '/lottieFiles/general/trending_up.lottie',
  },
  {
    title: 'Onboarding.AutoProcessingStep4Title',
    lottieSrc: '/lottieFiles/general/contract_edit.lottie',
  },
];

type Props = {
  applicationUuid: string;
  clientId: number;
};

export const ProcessingApplication = ({ clientId, applicationUuid }: Props) => {
  const theme = useTheme();
  const t = useTranslate();

  const { status } = useCardApplicationWebSocket(applicationUuid);
  const { currentStep, progress } = useFakeProgress(
    steps.length,
    status !== null
  );
  const isAutoApprovalComplete = status !== null && progress >= 100;

  const { title, lottieSrc } = steps[currentStep];

  if (isAutoApprovalComplete) {
    return <CardApplicationResult status={status} clientId={clientId} />;
  }

  return (
    <StandardPageContainer
      $isInIframe={isInIframe}
      $background={
        isInIframe
          ? undefined
          : theme.fn.themeColor('Container/Option_2_(White)')
      }
      style={{
        padding: '79px 32px',
        justifyContent: 'center',
      }}
      data-testid="card_application_confirmation_page"
    >
      <Stack align="center" spacing="xxl" maw="600px">
        <H4 color="Text/Headlines_(Brand)">
          {t('Onboarding.AutoProcessingTitle')}
        </H4>
        <DotLottieReact
          style={{ width: '160px', height: '160px' }}
          src={lottieSrc}
          loop
          autoplay
        />
        <DotsStepper
          title={t(title) as string}
          currentStep={currentStep}
          totalSteps={steps.length}
        />
        <ProgressBar
          label={t('Onboarding.AutoProcessingProgressLabel') as string}
          progress={progress}
        />
      </Stack>
    </StandardPageContainer>
  );
};
